<template>
  <div class="knowledge-qa-container" v-if="visible" @click.self="close">
    <div class="knowledge-qa-modal" @click.stop>
      <!-- 头部 -->
      <div class="qa-header">
        <div class="header-title">
          <span class="title-icon">🤖</span>
          <span class="title-text">黄河古贤工程设计知识问答系统</span>
        </div>
        <button class="close-btn" @click="close">×</button>
      </div>

      <!-- 内容区域 -->
      <div class="qa-content">
        <!-- 左侧：问答历史 -->
        <div class="chat-history">
          <div class="history-header">
            <span>查询历史</span>
            <button class="clear-btn" @click="clearHistory">清空</button>
          </div>
          <div class="history-list">
            <div
                v-for="(item, index) in history"
                :key="index"
                class="history-item"
                :class="{ active: activeHistory === index }"
                @click="loadHistory(index)"
            >
              <span class="history-icon">{{ item.icon }}</span>
              <span class="history-text">{{ item.title }}</span>
            </div>
            <!-- 专业设计问题 -->
            <div class="preset-questions">
              <div class="preset-title">典型设计问题：</div>
              <div
                  v-for="(q, idx) in presetQuestions"
                  :key="'preset-' + idx"
                  class="preset-item"
                  @click="selectPresetQuestion(q)"
              >
                {{ q }}
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧：对话区域 -->
        <div class="chat-area">
          <!-- 消息列表 -->
          <div class="messages-container" ref="messagesContainer">
            <div v-for="(message, index) in messages" :key="index" class="message-wrapper">
              <!-- 用户消息 -->
              <div v-if="message.role === 'user'" class="message user-message">
                <div class="message-avatar">👤</div>
                <div class="message-content">
                  <div class="message-text">{{ message.content }}</div>
                  <div class="message-time">{{ message.time }}</div>
                </div>
              </div>

              <!-- AI消息 -->
              <div v-if="message.role === 'assistant'" class="message assistant-message">
                <div class="message-avatar">🤖</div>
                <div class="message-content">
                  <!-- 知识卡片 -->
                  <div v-if="message.type === 'knowledge'" class="knowledge-card">
                    <div class="card-header">
                      <span class="card-title">📚 知识检索结果</span>
                      <span class="card-subtitle">检索到相关规范条文、工程案例、专家意见</span>
                    </div>

                    <!-- 综合设计建议（放在最前面） -->
                    <div class="card-summary" v-if="message.data.summary">
                      <div class="summary-title">
                        <span class="summary-icon">💡</span>
                        综合设计建议
                      </div>
                      <div class="summary-content">
                        <div class="summary-text">{{ message.data.summary }}</div>
                        <div class="summary-actions" v-if="message.data.actions">
                          <div class="action-title">建议措施：</div>
                          <ul class="action-list">
                            <li v-for="(action, aIdx) in message.data.actions" :key="'action-'+aIdx">{{ action }}</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <!-- 设计规范条文 -->
                    <div class="card-section" v-if="message.data.regulations.length > 0">
                      <div class="section-title">
                        <span class="section-icon">📋</span>
                        相关设计规范条文
                      </div>
                      <div class="section-content">
                        <div v-for="(item, idx) in message.data.regulations" :key="'reg-'+idx" class="regulation-item">
                          <div class="regulation-header">
                            <span class="regulation-title">{{ item.title }}</span>
                            <span class="regulation-code">{{ item.code }}</span>
                          </div>
                          <div class="regulation-clause">
                            <span class="clause-label">条款：</span>
                            <span class="clause-content">{{ item.clause }}</span>
                          </div>
                          <div class="regulation-content">{{ item.content }}</div>
                          <div class="regulation-requirements" v-if="item.requirements">
                            <div class="req-title">具体要求：</div>
                            <ul class="req-list">
                              <li v-for="(req, reqIdx) in item.requirements" :key="'req-'+reqIdx">{{ req }}</li>
                            </ul>
                          </div>
                          <div class="regulation-source">
                            <span>规范来源：{{ item.source }}</span>
                            <span class="regulation-year">（{{ item.year }}）</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 工程案例参考 -->
                    <div class="card-section" v-if="message.data.cases.length > 0">
                      <div class="section-title">
                        <span class="section-icon">🏗️</span>
                        类似工程案例
                      </div>
                      <div class="section-content">
                        <div v-for="(item, idx) in message.data.cases" :key="'case-'+idx" class="case-item">
                          <div class="case-header">
                            <div class="case-name-section">
                              <span class="case-name">{{ item.name }}</span>
                              <span class="case-location">（{{ item.location }}）</span>
                            </div>
                            <div class="case-tags">
                              <span class="case-type">{{ item.type }}</span>
                              <span class="case-scale">规模：{{ item.scale }}</span>
                            </div>
                          </div>
                          <div class="case-desc">{{ item.description }}</div>
                          <div class="case-params" v-if="item.parameters">
                            <div class="param-title">主要技术参数：</div>
                            <div class="param-grid">
                              <div v-for="(param, pIdx) in item.parameters" :key="'param-'+pIdx" class="param-item">
                                <span class="param-name">{{ param.name }}：</span>
                                <span class="param-value">{{ param.value }}</span>
                              </div>
                            </div>
                          </div>
                          <div class="case-features">
                            <span v-for="(feature, fIdx) in item.features" :key="'feature-'+fIdx" class="feature-tag">
                              {{ feature }}
                            </span>
                          </div>
                          <div class="case-lessons" v-if="item.lessons">
                            <div class="lessons-title">经验教训：</div>
                            <div class="lessons-content">{{ item.lessons }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 专家经验建议 -->
                    <div class="card-section" v-if="message.data.expertAdvice.length > 0">
                      <div class="section-title">
                        <span class="section-icon">👨‍💼</span>
                        专家经验建议
                      </div>
                      <div class="section-content">
                        <div v-for="(item, idx) in message.data.expertAdvice" :key="'advice-'+idx" class="advice-item">
                          <div class="advice-header">
                            <span class="advice-expert">{{ item.expert }}</span>
                            <span class="advice-title">{{ item.title }}</span>
                            <span class="advice-unit">（{{ item.unit }}）</span>
                          </div>
                          <div class="advice-content">{{ item.content }}</div>
                          <div class="advice-basis" v-if="item.basis">
                            <div class="basis-title">依据：</div>
                            <div class="basis-content">{{ item.basis }}</div>
                          </div>
                          <div class="advice-recommendations" v-if="item.recommendations">
                            <div class="rec-title">具体建议：</div>
                            <ul class="rec-list">
                              <li v-for="(rec, recIdx) in item.recommendations" :key="'rec-'+recIdx">{{ rec }}</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 普通文本回复 -->
                  <div v-else-if="message.type === 'structured'" class="structured-answer-card">
                    <div class="structured-header">
                      <div>
                        <div class="structured-title">{{ message.data.title }}</div>
                        <div class="structured-subtitle">{{ message.data.subtitle }}</div>
                      </div>
                      <div class="structured-tools">
                        <button class="voice-brief-btn" @click="speakBrief(message.data.voiceBrief)">播报摘要</button>
                        <button
                            v-if="!message.data.isReport"
                            class="voice-brief-btn"
                            @click="generateReportDraft"
                        >
                          生成报告草稿
                        </button>
                      </div>
                    </div>
                    <div class="structured-summary">{{ message.data.summary }}</div>
                    <div class="structured-grid">
                      <div
                          v-for="(section, sectionIdx) in message.data.sections"
                          :key="'structured-section-' + sectionIdx"
                          class="structured-section"
                      >
                        <div class="structured-section-title">{{ section.title }}</div>
                        <ul class="structured-list">
                          <li v-for="(item, itemIdx) in section.items" :key="'structured-item-' + itemIdx">
                            {{ item }}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div v-if="message.citations && message.citations.length" class="structured-citations">
                      <span>引用依据</span>
                      <div v-for="(citation, citationIdx) in message.citations" :key="'citation-' + citationIdx">
                        [{{ citationIdx + 1 }}] {{ citation.document_title || citation.document_id }} / {{ citation.chunk_id }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="message-text">{{ message.content }}</div>
                  <div v-if="message.actions && message.actions.length" class="rag-actions">
                    <button
                        v-for="(action, actionIdx) in message.actions"
                        :key="'rag-action-' + actionIdx"
                        class="rag-action-btn"
                        :title="action.reason"
                        @click="runKnowledgeAction(action)"
                    >
                      {{ action.label }}
                    </button>
                  </div>

                  <div class="message-time">{{ message.time }}</div>
                </div>
              </div>
            </div>

            <!-- 加载中 -->
            <div v-if="loading" class="loading-message">
              <div class="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span class="loading-text">正在查询规范库、案例库、专家库...</span>
            </div>
          </div>

          <!-- 输入区域 -->
          <div class="input-area">
            <div class="input-tools">
              <button
                  v-for="(tool, idx) in visibleInputTools"
                  :key="'tool-'+idx"
                  class="tool-btn"
                  :title="tool.title"
                  :disabled="loading"
                  @click="useTool(tool.action)"
              >
                {{ tool.icon }}
              </button>
            </div>
            <div v-if="showIngestPanel" class="ingest-panel">
              <div class="ingest-header">
                <span class="ingest-title">导入知识资料</span>
                <button class="ingest-close-btn" @click="showIngestPanel = false">关闭</button>
              </div>
              <div class="ingest-grid">
                <input
                    v-model="ingestTitle"
                    class="ingest-input"
                    placeholder="资料标题（可选）"
                />
                <input
                    v-model="ingestPath"
                    class="ingest-input"
                    placeholder="服务器可读取的文件路径，例如 D:/aaa_hydro/.../方案.docx"
                />
              </div>
              <textarea
                  v-model="ingestText"
                  class="ingest-textarea"
                  placeholder="或直接粘贴文本内容。填写路径和文本时优先导入文本。"
                  rows="3"
              ></textarea>
              <input
                  ref="ingestFileInput"
                  type="file"
                  class="ingest-file-input"
                  accept=".txt,.md,.csv,.json,.yaml,.yml,.docx,.pptx,.xlsx,.xlsm,.pdf"
                  @change="onIngestFileChange"
              />
              <div class="ingest-file-row">
                <span>{{ ingestFile ? ingestFile.name : '未选择浏览器文件' }}</span>
                <button class="ingest-file-btn" @click="selectIngestFile">选择文件</button>
                <button v-if="ingestFile" class="ingest-file-btn ghost" @click="clearIngestFile">清除</button>
              </div>
              <div class="ingest-actions">
                <span class="ingest-status">{{ ingestStatus }}</span>
                <button class="ingest-submit-btn" @click="ingestKnowledge" :disabled="ingestLoading">
                  {{ ingestLoading ? '导入中...' : '导入' }}
                </button>
              </div>
            </div>
            <div class="input-wrapper">
              <textarea
                  v-model="userInput"
                  placeholder="请输入设计问题，如：混凝土重力坝防渗设计有哪些规范要求？"
                  @keydown.enter.exact.prevent="sendMessage"
                  @keydown.enter.shift.exact.prevent="userInput += '\n'"
                  rows="3"
                  class="message-input"
                  ref="messageInput"
              ></textarea>
              <button class="send-btn" @click="sendMessage" :disabled="!userInput.trim()">
                <span class="send-icon">↗</span>
              </button>
            </div>
            <div class="quick-actions">
              <span class="quick-label">快速检索：</span>
              <button
                  v-for="(action, idx) in quickActions"
                  :key="'action-'+idx"
                  class="quick-btn"
                  @click="quickQuery(action.query)"
              >
                {{ action.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { askKnowledge } from '@/services/llmService.js'
import { panelStore } from '@/panelStore.js'
import { ingestKnowledgeDocument, queryKnowledge, requireKnowledgeApi, toKnowledgeMessage, uploadKnowledgeFile } from '@/services/knowledgeService.js'

export default {
  name: 'KnowledgeQA',
  inject: {
    voiceService: { default: null }
  },
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    initialQuery: {
      type: String,
      default: ''
    },
    source: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      userInput: '',
      messages: [
        {
          role: 'user',
          content: '重力坝抗滑稳定计算应采用哪些安全系数？',
          time: '10:15'
        },
        {
          role: 'assistant',
          type: 'knowledge',
          content: '根据您关于重力坝抗滑稳定计算安全系数的问题，检索到以下相关知识：',
          time: '10:16',
          data: {
            regulations: [
              {
                title: '混凝土重力坝设计规范',
                code: 'SL 319-2005',
                clause: '第6.3.2条',
                content: '重力坝抗滑稳定计算采用安全系数法，主要有抗剪强度公式（摩擦公式）和抗剪断强度公式两种方法。',
                requirements: [
                  '抗剪强度公式（摩擦公式）：Ks = (f∑W - U)/∑P，仅考虑摩擦力',
                  '抗剪断强度公式：K′s = (f′∑W - U + c′A)/∑P，同时考虑摩擦力和凝聚力',
                  '1级建筑物基本组合：K≥2.5，特殊组合可适当降低但不低于2.0',
                  '采用概率分析方法时，可靠度指标β≥3.7'
                ],
                source: '水利行业标准',
                year: '2005'
              },
              {
                title: '混凝土重力坝设计规范',
                code: 'SL 319-2018',
                clause: '第6.4.2条',
                content: '坝基岩体内存在软弱结构面、缓倾角裂隙时，应核算深层抗滑稳定。',
                requirements: [
                  '采用刚体极限平衡法计算为主，高坝或地质条件复杂的中坝宜辅以有限元法',
                  '根据滑动面分布情况分为单滑面、双滑面和多滑面计算模式',
                  '抗剪断强度公式计算的K′值需满足规范规定的最小值',
                  '采取工程措施后仍不满足时可按抗剪强度公式计算'
                ],
                source: '水利行业标准',
                year: '2018'
              }
            ],
            cases: [
              {
                name: '某碾压混凝土重力坝工程',
                location: '西南地区',
                type: '碾压混凝土重力坝',
                scale: '中型',
                description: '该工程最大坝高123m，坝址区有断层和泥化夹层，采用刚体极限平衡法和有限元强度储备法进行抗滑稳定分析。',
                parameters: [
                  { name: '最大坝高', value: '123m' },
                  { name: '分析方法', value: '刚体极限平衡法+有限元法' },
                  { name: '安全系数（正常工况）', value: '>3.0' },
                  { name: '安全系数（校核工况）', value: '>2.5' }
                ],
                features: ['复杂地质条件', '三维有限元分析', '强度储备法'],
                lessons: '采用多种方法相互验证，对复杂地质条件下的抗滑稳定分析更为可靠'
              },
              {
                name: '峡谷水利枢纽工程',
                location: '黄河流域',
                type: '碾压混凝土重力坝',
                scale: '大型',
                description: '针对"千层饼"地质难题，采取深齿槽、抗剪洞、压重等措施增加摩擦力，确保大坝抗滑稳定。',
                parameters: [
                  { name: '混凝土浇筑量', value: '超1800万m³' },
                  { name: '勘探钻孔', value: '近8万米' },
                  { name: '抗滑措施', value: '深齿槽+抗剪洞' },
                  { name: '月浇筑强度', value: '55万m³' }
                ],
                features: ['复杂地基处理', '高强度施工', '综合抗滑措施'],
                lessons: '针对特殊地质条件需采取综合工程措施，勘探工作对设计方案确定至关重要'
              }
            ],
            expertAdvice: [
              {
                expert: '张教授',
                title: '水工结构专家',
                unit: '河海大学',
                content: '抗滑稳定分析应采用刚体极限平衡法与有限元法相互验证，特别对于存在软弱夹层的坝基，需重点分析深层滑动模式。',
                basis: '基于多个重力坝工程实践与研究成果',
                recommendations: [
                  '优先采用抗剪断公式计算安全系数，计入黏聚力影响',
                  '复杂地基时采用有限元强度折减法或应力代数和法',
                  '对软弱夹层参数进行敏感性分析',
                  '考虑地震、洪水等特殊工况的荷载组合'
                ]
              },
              {
                expert: '李总工',
                title: '设计大师',
                unit: '长江水利委员会',
                content: '安全系数的选取需与工程措施相结合，如设置齿墙、防渗板、锚固桩等，提高实际安全储备。',
                basis: '多个水利工程实践经验',
                recommendations: [
                  '根据地质条件选择适当的抗剪强度参数',
                  '采用分项系数极限状态设计法提高可靠性',
                  '加强施工期监测和运行期复核',
                  '重点监控坝踵和坝趾的应力状态'
                ]
              }
            ],
            summary: '重力坝抗滑稳定安全系数需根据建筑物级别、荷载组合和计算方法确定。规范要求采用抗剪断公式计算时基本组合下不低于3.0，特殊组合下不低于2.5。深层抗滑稳定需采用有限元法等专项分析。',
            actions: [
              '严格执行SL 319-2005规范的安全系数要求',
              '采用刚体极限平衡法进行初步计算，并结合有限元法验证',
              '对坝基软弱夹层进行专项勘察和参数测试',
              '考虑三维效应和坝段协同作用的影响',
              '通过工程措施（如齿墙、锚固）提高实际安全度'
            ]
          }
        }
      ],
      history: [
        { icon: '📋', title: '重力坝防渗设计规范', time: '10:30' },
        { icon: '🏗️', title: '导流洞布置方案', time: '昨天' },
        { icon: '💧', title: '水库调度规程编制', time: '前天' },
        { icon: '📊', title: '混凝土配合比设计', time: '2024-01-15' }
      ],
      activeHistory: null,
      loading: false,
      showIngestPanel: false,
      ingestTitle: '',
      ingestPath: '',
      ingestText: '',
      ingestFile: null,
      ingestLoading: false,
      ingestStatus: '',
      lastInitialQuery: '',
      presetQuestions: [
        '重力坝抗滑稳定计算应采用哪些安全系数？',
        '导流洞布置应考虑哪些地质条件？',
        '水库正常蓄水位如何确定？',
        '施工期防洪标准如何选定？',
        '混凝土重力坝温度控制有哪些要求？',
        '泄洪消能设施设计标准是什么？',
        '工程边坡稳定分析采用什么方法？',
        '金属结构防腐设计寿命要求多少年？',
        '生态流量下泄有哪些规定？',
        '施工导流洪水重现期如何确定？'
      ],
      inputTools: [
        { icon: '📁', title: '上传设计文件', action: 'upload' },
        { icon: '📍', title: '标注设计位置', action: 'location' },
        { icon: '🔍', title: '高级语义检索', action: 'search' },
        { icon: '📊', title: '数据分析', action: 'analysis' },
        { icon: '📄', title: '生成报告', action: 'report' }
      ],
      quickActions: [
        { label: '规范查询', query: '请列出相关设计规范' },
        { label: '案例推荐', query: '推荐类似工程案例' },
        { label: '专家建议', query: '专家对此有什么建议' },
        { label: '技术参数', query: '关键技术参数要求' },
        { label: '审查要点', query: '设计审查注意事项' }
      ],
      historyAnswerMap: {
        '重力坝防渗设计规范': this.generateSeepageDesignAnswer,
        '导流洞布置方案': this.generateDiversionLayoutAnswer,
        '水库调度规程编制': this.generateReservoirOperationAnswer,
        '混凝土配合比设计': this.generateConcreteMixAnswer
      }
    }
  },
  computed: {
    visibleInputTools() {
      return this.inputTools.filter(tool => tool.action !== 'report')
    }
  },
  watch: {
    initialQuery: {
      immediate: true,
      handler(val) {
        const query = String(val || '').trim()
        if (!query || query === this.lastInitialQuery) return
        this.lastInitialQuery = query
        this.$nextTick(() => {
          this.userInput = query
          this.sendMessage()
        })
      }
    },
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.scrollToBottom()
          this.$refs.messageInput?.focus()
        })
      }
    },
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      },
      deep: true
    }
  },
  methods: {
    getCurrentTime() {
      const now = new Date()
      return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    },

    buildConversationContext() {
      return this.messages
        .filter(item => item.role === 'user' || item.role === 'assistant')
        .slice(-8)
        .map(item => `${item.role === 'user' ? '用户' : '助手'}：${item.content || item.data?.summary || ''}`)
        .filter(Boolean)
    },

    async resolveKnowledgeAnswer(input) {
      try {
        const ragReply = await queryKnowledge(input, {
          domain: 'engineering_design',
          context: this.buildConversationContext()
        })
        if (ragReply && Array.isArray(ragReply.citations) && ragReply.citations.length > 0) {
          return this.toStructuredReply(input, toKnowledgeMessage(ragReply))
        }
      } catch (error) {
        if (requireKnowledgeApi()) throw error
        console.warn('[KnowledgeQA] RAG unavailable, trying LLM fallback:', error.message)
      }
      if (requireKnowledgeApi()) throw new Error('Knowledge API returned no citations in strict API mode')
      return this.toStructuredReply(input, { content: await askKnowledge(input), citations: [], actions: [] })
    },

    async sendMessage() {
      const input = this.userInput.trim()
      if (!input) return

      this.messages.push({
        role: 'user',
        content: input,
        time: this.getCurrentTime()
      })

      this.userInput = ''
      this.loading = true
      this.addToHistory(input)

      try {
        const reply = await this.resolveKnowledgeAnswer(input)
        this.messages.push({
          role: 'assistant',
          type: reply.type || undefined,
          content: reply.content,
          data: reply.data,
          citations: reply.citations,
          actions: reply.actions,
          time: this.getCurrentTime()
        })
        if (reply.data && reply.data.voiceBrief) this.speakBrief(reply.data.voiceBrief)
      } catch (e) {
        console.warn('[KnowledgeQA] LLM失败，使用本地回复:', e.message)
        this.generateProfessionalResponse(input)
      }

      this.loading = false
      this.scrollToBottom()
    },

    toStructuredReply(question, reply) {
      const content = String(reply.content || '')
      const sections = this.extractAnswerSections(content, question)
      const summary = this.extractSummary(content, question)
      return {
        type: 'structured',
        content,
        data: {
          title: this.inferAnswerTitle(question),
          subtitle: '工程设计知识问答 · 已结合当前上下文整理',
          summary,
          voiceBrief: this.buildVoiceBrief(summary, sections),
          sections,
        },
        citations: reply.citations || [],
        actions: reply.actions || [],
      }
    },

    inferAnswerTitle(question) {
      if (/规范|规程|标准/.test(question)) return '相关设计规范与依据'
      if (/安全系数|抗滑|稳定/.test(question)) return '抗滑稳定计算要点'
      if (/报告|审查|符合性/.test(question)) return '规范符合性报告'
      return '工程设计知识答复'
    },

    extractSummary(content, question) {
      if (this.isGravityDamStabilityStandardsQuestion(question)) {
        return '重力坝抗滑稳定相关依据应以《混凝土重力坝设计规范》为核心，配合工程等级、荷载、地质勘察、岩石试验和结构可靠性标准共同使用。'
      }
      const cleaned = this.cleanAnswerText(content)
      const firstSentence = cleaned.split(/[。！？\n]/).find(Boolean)
      if (firstSentence && firstSentence.length >= 12) return `${firstSentence}。`
      if (/抗滑|稳定/.test(question)) {
        return '重力坝抗滑稳定应以混凝土重力坝设计规范为核心依据，结合工程等级、荷载组合、坝基地质参数和深层滑动模式综合验算。'
      }
      return '已按工程设计问答场景整理为结构化结论，建议继续补充工程部位、设计阶段和需要核查的规范条文。'
    },

    extractAnswerSections(content, question) {
      if (this.isGravityDamStabilityStandardsQuestion(question)) {
        return this.buildGravityDamStabilityStandards()
      }
      const lines = this.cleanAnswerText(content).split('\n').map(item => item.trim()).filter(Boolean)
      const sections = []
      let current = null
      lines.forEach(line => {
        const heading = line.match(/^(?:\d+[\.、]\s*)?([^：:]{2,24})[：:]$/)
        if (heading) {
          current = { title: heading[1], items: [] }
          sections.push(current)
          return
        }
        const item = line.replace(/^[-*]\s*/, '').replace(/^\d+[\.、]\s*/, '').trim()
        if (!item) return
        if (!current) {
          current = { title: '核心结论', items: [] }
          sections.push(current)
        }
        current.items.push(item)
      })
      if (sections.length <= 1 && /抗滑|稳定|规范/.test(question)) {
        return [
          { title: '核心规范', items: ['《混凝土重力坝设计规范》是抗滑稳定验算的核心依据，应重点核查稳定分析章节、抗剪断强度公式和深层抗滑条文。'] },
          { title: '配套依据', items: ['工程等级和洪水标准用于确定建筑物级别与校核工况。', '地质勘察规范用于确定坝基岩体、软弱夹层和抗剪强度参数。', '结构可靠性统一标准用于可靠度或分项系数法复核。'] },
          { title: '古贤提示', items: ['古贤工程展示时应突出高含沙水流、复杂岩体结构、深层抗滑和坝段协同作用。', '汇报中建议把“规范依据 -> 参数来源 -> 计算方法 -> 安全系数 -> 工程措施”串成闭环。'] },
        ]
      }
      return sections.slice(0, 5).map(section => ({ ...section, items: section.items.slice(0, 5) }))
    },

    isGravityDamStabilityStandardsQuestion(question) {
      return /重力坝|抗滑|稳定/.test(question) && /规范|规程|标准|依据|列出/.test(question)
    },

    buildGravityDamStabilityStandards() {
      return [
        {
          title: '核心设计规范',
          items: [
            '《混凝土重力坝设计规范》（SL 319-2018）：抗滑稳定验算的核心依据，重点核查稳定分析、抗剪断强度公式、抗剪强度公式、坝基深层滑动和岸坡坝段稳定。',
            '《混凝土重力坝设计规范》（NB/T 35026-2022，项目资料采用时）：可作为新版能源行业口径下的重力坝稳定复核依据，需与项目采用标准体系保持一致。',
          ]
        },
        {
          title: '工程等级与工况依据',
          items: [
            '《水利水电工程等级划分及洪水标准》（SL 252-2017）：用于确定工程等别、建筑物级别以及设计洪水、校核洪水等稳定验算工况。',
            '《水工建筑物荷载设计规范》（SL 744-2016）：用于整理自重、水压力、扬压力、淤沙压力、浪压力、地震作用等荷载取值。',
          ]
        },
        {
          title: '地质与参数依据',
          items: [
            '《水利水电工程地质勘察规范》（GB 50487-2008）：用于确定坝基岩体、软弱夹层、缓倾角结构面及抗剪强度参数。',
            '《水利水电工程岩石试验规程》（SL/T 264-2020）：用于支撑岩体、结构面抗剪强度和物理力学参数试验取值。',
          ]
        },
        {
          title: '可靠度与复核依据',
          items: [
            '《水利水电工程结构可靠性设计统一标准》（GB 50199-2013）：采用可靠度或分项系数方法复核时使用。',
            '复杂坝基或存在深层滑动风险时，应在规范安全系数法基础上结合刚体极限平衡法、有限元法或强度折减法进行专项复核。',
          ]
        },
        {
          title: '古贤汇报口径',
          items: [
            '汇报时建议按“工程级别 -> 荷载组合 -> 参数来源 -> 计算方法 -> 安全系数 -> 工程措施”展开。',
            '古贤工程应重点说明高含沙水流、复杂岩体结构、软弱结构面和深层抗滑稳定对设计安全储备的影响。',
          ]
        },
      ]
    },

    cleanAnswerText(content) {
      return String(content || '')
        .replace(/\*\*/g, '')
        .replace(/\$([^$]+)\$/g, '$1')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    },

    buildVoiceBrief(summary, sections) {
      const firstPoint = sections && sections[0] && sections[0].items ? sections[0].items[0] : ''
      return [summary, firstPoint].filter(Boolean).join('。').replace(/。+/g, '。').slice(0, 180)
    },

    speakBrief(text) {
      const brief = String(text || '').trim()
      if (!brief || !this.voiceService || typeof this.voiceService.speak !== 'function') return
      this.voiceService.speak(brief)
    },

    generateProfessionalResponse(query) {
      const responses = {
        '抗滑稳定': this.generateStabilityResponse,
        '导流': this.generateDiversionLayoutAnswer,
        '水库': this.generateReservoirOperationAnswer,
        '防洪': this.generateFloodControlResponse,
        '混凝土': this.generateConcreteMixAnswer,
        '泄洪': this.generateSpillwayResponse,
        '边坡': this.generateSlopeResponse,
        '防腐': this.generateCorrosionResponse,
        '生态': this.generateEcologyResponse,
        '施工': this.generateConstructionResponse,
        '防渗': this.generateSeepageDesignAnswer,
        '重力坝': this.generateStabilityResponse,
        '参数': this.generateParameterResponse
      }

      let responseGenerator = this.generateGeneralResponse

      for (const [keyword, generator] of Object.entries(responses)) {
        if (query.includes(keyword)) {
          responseGenerator = generator
          break
        }
      }

      const response = responseGenerator(query)
      this.messages.push(response)
    },

    generateSeepageDesignAnswer() {
      return {
        role: 'assistant',
        type: 'knowledge',
        content: '根据您关于重力坝防渗设计规范的问题，检索到以下相关信息：',
        time: this.getCurrentTime(),
        data: {
          regulations: [
            {
              title: '水利水电工程防渗墙设计规范',
              code: 'SL 379-2007',
              clause: '第3.2.3条',
              content: '防渗墙设计应满足抗渗稳定、结构安全和耐久性要求，渗透比降应根据工程等级和水文地质条件确定。',
              requirements: [
                'Ⅰ、Ⅱ级建筑物渗透比降不应大于3.0',
                'Ⅲ、Ⅳ级建筑物渗透比降不应大于4.0',
                '防渗墙厚度应满足强度和变形要求',
                '接头设计应保证防渗连续性和整体性'
              ],
              source: '水利行业标准',
              year: '2007'
            },
            {
              title: '碾压式土石坝设计规范',
              code: 'SL 274-2001',
              clause: '第5.4.2条',
              content: '防渗体与岸坡及刚性建筑物连接时，应采用能适应变形的结构形式和止水措施。',
              requirements: [
                '防渗体与岸坡连接坡度不陡于1:0.5',
                '连接处应扩大断面或设齿墙',
                '采用可靠止水措施',
                '进行接触面渗流计算'
              ],
              source: '水利行业标准',
              year: '2001'
            }
          ],
          cases: [
            {
              name: '某高土石坝工程',
              location: '西南山区',
              type: '土石坝',
              scale: '大（1）型',
              description: '该坝采用混凝土防渗墙，最大深度达150m，有效控制深厚覆盖层渗漏问题。',
              parameters: [
                { name: '最大坝高', value: '186m' },
                { name: '防渗墙深度', value: '150m' },
                { name: '墙体厚度', value: '1.2m' },
                { name: '渗透系数', value: '≤1×10⁻⁷cm/s' }
              ],
              features: ['深厚覆盖层处理', '深混凝土防渗墙', '高压灌浆'],
              lessons: '深厚覆盖层地区需采用深防渗墙结合高压灌浆的综合防渗方案'
            },
            {
              name: '某面板堆石坝工程',
              location: '西北地区',
              type: '面板堆石坝',
              scale: '中型',
              description: '采用挤压边墙施工技术，提高面板混凝土施工质量，有效控制渗漏。',
              parameters: [
                { name: '坝高', value: '110m' },
                { name: '面板面积', value: '2.8万m²' },
                { name: '接缝数量', value: '35条' },
                { name: '渗漏量', value: '<20L/s' }
              ],
              features: ['挤压边墙技术', '面板防裂', '接缝止水'],
              lessons: '挤压边墙技术可提高面板基础质量，减少面板裂缝'
            }
          ],
          expertAdvice: [
            {
              expert: '王总工',
              title: '防渗设计专家',
              unit: '黄河水利委员会',
              content: '防渗设计需根据地质条件选择合适方案，深厚覆盖层地区宜采用混凝土防渗墙结合灌浆帷幕的综合措施。',
              basis: '基于多个高坝工程防渗处理经验',
              recommendations: [
                '采用"堵、排、截"结合的防渗体系',
                '帷幕灌浆深度应达到相对不透水层',
                '设置完善的排水降压系统',
                '加强渗流监测和预警'
              ]
            }
          ],
          summary: '重力坝防渗设计应以渗流控制为核心，采用帷幕灌浆与排水系统相结合的综合防渗方案，严格执行相关规范要求。',
          actions: [
            '严格执行SL 379-2007、SL 274-2001等规范',
            '采用帷幕灌浆与排水系统结合的综合防渗',
            '加强渗流监测和预警',
            '考虑地质条件对渗流的影响'
          ]
        }
      }
    },

    generateDiversionLayoutAnswer() {
      return {
        role: 'assistant',
        type: 'knowledge',
        content: '根据您关于导流洞布置方案的问题，检索到以下相关信息：',
        time: this.getCurrentTime(),
        data: {
          regulations: [
            {
              title: '水利水电工程施工导流设计规范',
              code: 'SL 303-2017',
              clause: '第4.2.2条',
              content: '导流洞布置应根据地形地质条件、施工进度、导流标准等综合确定，避开不良地质段。',
              requirements: [
                '导流洞轴线应尽量顺直，减少转弯',
                '洞顶围岩厚度应满足稳定性要求',
                '洞口位置应避开滑坡、崩塌等不良地质',
                '洞线应满足施工期和运行期的要求'
              ],
              source: '水利行业标准',
              year: '2017'
            }
          ],
          cases: [
            {
              name: '某高山峡谷水电站',
              location: '西南高山峡谷',
              type: '导流洞工程',
              scale: '大型',
              description: '针对复杂地质条件，采用"一洞两用"方案，导流洞后期改建为泄洪洞，节约投资。',
              parameters: [
                { name: '导流洞尺寸', value: '16m×20m' },
                { name: '长度', value: '1.2km' },
                { name: '地质条件', value: 'Ⅳ类围岩为主' },
                { name: '施工方法', value: '钻爆法+TBM结合' }
              ],
              features: ['一洞两用', '复杂地质施工', 'TBM应用'],
              lessons: '导流洞与永久建筑物结合可节约投资，但需提前规划好改建方案'
            },
            {
              name: '某抽水蓄能电站',
              location: '东部山区',
              type: '导流洞工程',
              scale: '中型',
              description: '采用小断面导流洞结合围堰的导流方案，适应狭窄河谷地形。',
              parameters: [
                { name: '导流洞断面', value: '8m×10m' },
                { name: '长度', value: '850m' },
                { name: '最大流速', value: '15m/s' },
                { name: '导流标准', value: '10年一遇' }
              ],
              features: ['小断面导流', '狭窄河谷', '高流速'],
              lessons: '狭窄河谷地区可采用小断面导流洞，但需注意高流速带来的空蚀问题'
            }
          ],
          expertAdvice: [
            {
              expert: '陈教授',
              title: '施工导流专家',
              unit: '武汉大学',
              content: '导流洞布置应优先考虑地质条件，避开断层、破碎带等不良地质段。',
              basis: '多个大型水利工程施工导流经验',
              recommendations: [
                '优先选择地质条件良好的区域',
                '洞线布置应考虑后期封堵',
                '进出口水流条件应平顺',
                '应设置必要的施工支洞'
              ]
            }
          ],
          summary: '导流洞布置应综合地质、水文、施工等因素，确保施工期安全和运行可靠。',
          actions: [
            '严格执行SL 303-2017规范',
            '优先考虑地质条件良好的洞线',
            '优化进出口水流条件',
            '为后期封堵创造条件'
          ]
        }
      }
    },

    generateReservoirOperationAnswer() {
      return {
        role: 'assistant',
        type: 'knowledge',
        content: '根据您关于水库调度规程编制的问题，检索到以下相关信息：',
        time: this.getCurrentTime(),
        data: {
          regulations: [
            {
              title: '水库调度规程编制导则',
              code: 'SL 706-2015',
              clause: '第3.1.2条',
              content: '调度规程应明确防洪、兴利、生态等调度目标，制定不同频率洪水的调度方式。',
              requirements: [
                '明确防洪调度原则和方式',
                '制定兴利调度计划',
                '确定生态调度要求',
                '建立应急调度预案'
              ],
              source: '水利行业标准',
              year: '2015'
            }
          ],
          cases: [
            {
              name: '某山区水库',
              location: '西南山区',
              type: '年调节水库',
              scale: '中型',
              description: '以防洪、灌溉为主，兼顾发电，建立了基于气象预报的预泄调度模式。',
              parameters: [
                { name: '防洪库容', value: '1.2亿m³' },
                { name: '兴利库容', value: '0.8亿m³' },
                { name: '调度方式', value: '预报预泄' },
                { name: '生态流量', value: '3m³/s' }
              ],
              features: ['防洪调度', '灌溉调度', '预报预泄'],
              lessons: '基于气象预报的预泄调度可提高防洪效益，但需准确的水文预报支撑'
            },
            {
              name: '某平原水库',
              location: '华北平原',
              type: '平原水库',
              scale: '中型',
              description: '以城市供水为主，采用"高蓄低补"的调度方式，保障供水安全。',
              parameters: [
                { name: '总库容', value: '0.6亿m³' },
                { name: '供水规模', value: '20万t/d' },
                { name: '调度周期', value: '旬调度' },
                { name: '水质目标', value: 'Ⅱ类' }
              ],
              features: ['城市供水', '水质保障', '精细调度'],
              lessons: '平原水库需重点考虑水质变化，采用分层取水等措施保障供水水质'
            }
          ],
          expertAdvice: [
            {
              expert: '张博士',
              title: '水库调度专家',
              unit: '中国水利水电科学研究院',
              content: '调度规程编制应统筹协调各用水部门的需求，确保水资源高效利用。',
              basis: '多年水库调度实践经验',
              recommendations: [
                '建立多目标优化调度模型',
                '制定详细的调度图表',
                '建立调度决策支持系统',
                '定期修订调度规程'
              ]
            }
          ],
          summary: '水库调度规程编制应统筹防洪、兴利、生态等多目标，建立科学合理的调度方案。',
          actions: [
            '严格执行SL 706-2015导则',
            '建立多目标协调调度机制',
            '制定详细的调度图表',
            '建立调度决策支持系统'
          ]
        }
      }
    },

    generateConcreteMixAnswer() {
      return {
        role: 'assistant',
        type: 'knowledge',
        content: '根据您关于混凝土配合比设计的问题，检索到以下相关信息：',
        time: this.getCurrentTime(),
        data: {
          regulations: [
            {
              title: '水工混凝土施工规范',
              code: 'DL/T 5144-2015',
              clause: '第4.2.1条',
              content: '混凝土配合比设计应根据工程要求、原材料性能、施工条件等确定，满足强度、耐久性、工作性等要求。',
              requirements: [
                '抗压强度满足设计要求',
                '抗渗等级满足设计要求',
                '抗冻等级满足设计要求',
                '工作性满足施工要求'
              ],
              source: '电力行业标准',
              year: '2015'
            }
          ],
          cases: [
            {
              name: '某碾压混凝土坝',
              location: '西南地区',
              type: '碾压混凝土重力坝',
              scale: '大型',
              description: '采用粉煤灰、矿渣双掺技术，降低水泥用量，控制混凝土温升。',
              parameters: [
                { name: '水泥用量', value: '120kg/m³' },
                { name: '粉煤灰掺量', value: '30%' },
                { name: '矿渣掺量', value: '20%' },
                { name: 'VC值', value: '5-7s' }
              ],
              features: ['双掺技术', '低热水泥', '温控防裂'],
              lessons: '双掺技术可有效降低水化热，但需控制掺合料质量和掺量'
            },
            {
              name: '某高拱坝工程',
              location: '高山峡谷',
              type: '混凝土拱坝',
              scale: '大型',
              description: '采用四级配混凝土，优化骨料级配，提高混凝土抗裂性能。',
              parameters: [
                { name: '最大骨料粒径', value: '150mm' },
                { name: '砂率', value: '22%' },
                { name: '水胶比', value: '0.45' },
                { name: '坍落度', value: '50-70mm' }
              ],
              features: ['四级配混凝土', '骨料优化', '抗裂设计'],
              lessons: '优化骨料级配可提高混凝土密实度和抗裂性'
            }
          ],
          expertAdvice: [
            {
              expert: '李教授',
              title: '混凝土材料专家',
              unit: '清华大学',
              content: '配合比设计应考虑工程特点、原材料特性和施工条件，优化胶凝材料体系。',
              basis: '多年混凝土材料研究经验',
              recommendations: [
                '采用低热水泥和掺合料',
                '优化骨料级配',
                '控制水胶比',
                '加强温控防裂'
              ]
            }
          ],
          summary: '混凝土配合比设计应综合考虑强度、耐久性、工作性和经济性，满足工程实际需求。',
          actions: [
            '严格执行DL/T 5144-2015规范',
            '优化胶凝材料体系',
            '控制水胶比和坍落度',
            '加强温控防裂措施'
          ]
        }
      }
    },

    generateGeneralResponse(query) {
      return {
        role: 'assistant',
        content: `关于"${query}"，我从规范库、案例库和专家库中检索到相关信息。水利工程设计需综合考虑水文、地质、结构、施工等多方面因素。您是否需要更详细的某个方面的信息？`,
        time: this.getCurrentTime()
      }
    },

    generateFloodControlResponse() {
      return this.generateGeneralResponse('防洪设计')
    },

    generateSpillwayResponse() {
      return this.generateGeneralResponse('泄洪消能设计')
    },

    generateSlopeResponse() {
      return this.generateGeneralResponse('边坡稳定分析')
    },

    generateCorrosionResponse() {
      return this.generateGeneralResponse('金属结构防腐')
    },

    generateEcologyResponse() {
      return this.generateGeneralResponse('生态流量')
    },

    generateConstructionResponse() {
      return this.generateGeneralResponse('施工导流')
    },

    generateParameterResponse() {
      return this.generateGeneralResponse('技术参数')
    },

    addToHistory(query) {
      const title = query.length > 20 ? query.substring(0, 20) + '...' : query
      this.history.unshift({
        icon: '❓',
        title: title,
        time: this.getCurrentTime()
      })

      if (this.history.length > 10) {
        this.history.pop()
      }
    },

    loadHistory(index) {
      this.activeHistory = index
      const historyItem = this.history[index]

      this.messages = []

      this.messages.push({
        role: 'user',
        content: historyItem.title,
        time: historyItem.time
      })

      const answerGenerator = this.historyAnswerMap[historyItem.title] || this.generateGeneralResponse
      const response = answerGenerator.call(this)
      response.time = this.getCurrentTime()
      this.messages.push(response)

      this.scrollToBottom()
    },

    clearHistory() {
      this.history = []
    },

    selectPresetQuestion(question) {
      this.userInput = question
      this.$refs.messageInput?.focus()
    },

    quickQuery(query) {
      this.userInput = query
      this.sendMessage()
    },

    async ingestKnowledge() {
      const title = this.ingestTitle.trim()
      const path = this.ingestPath.trim()
      const text = this.ingestText.trim()

      if (!this.ingestFile && !path && !text) {
        this.ingestStatus = '请选择浏览器文件、填写服务器文件路径，或粘贴文本内容。'
        return
      }

      this.ingestLoading = true
      this.ingestStatus = ''

      try {
        let result
        if (this.ingestFile) {
          result = await uploadKnowledgeFile(this.ingestFile, { title: title || this.ingestFile.name })
        } else {
          const payload = { title: title || undefined }
          if (text) {
            payload.text = text
          } else {
            payload.path = path
          }
          result = await ingestKnowledgeDocument(payload)
        }
        const importedTitle = result.title || title || path || this.ingestFile?.name || '未命名资料'
        const chunkCount = Array.isArray(result.chunks) ? result.chunks.length : result.chunk_count

        this.ingestStatus = `已导入：${importedTitle}`
        this.messages.push({
          role: 'assistant',
          content: `知识资料已导入：${importedTitle}${chunkCount ? `，分片 ${chunkCount} 条` : ''}。现在可以直接提问检索。`,
          time: this.getCurrentTime()
        })

        this.ingestTitle = ''
        this.ingestPath = ''
        this.ingestText = ''
        this.clearIngestFile()
        this.showIngestPanel = false
      } catch (error) {
        this.ingestStatus = `导入失败：${error.message}`
        this.messages.push({
          role: 'assistant',
          content: `知识资料导入失败：${error.message}`,
          time: this.getCurrentTime()
        })
      } finally {
        this.ingestLoading = false
      }
    },

    selectIngestFile() {
      this.$refs.ingestFileInput?.click()
    },

    onIngestFileChange(event) {
      const files = event.target.files
      this.ingestFile = files && files.length ? files[0] : null
      if (this.ingestFile && !this.ingestTitle) {
        this.ingestTitle = this.ingestFile.name.replace(/\.[^.]+$/, '')
      }
    },

    clearIngestFile() {
      this.ingestFile = null
      if (this.$refs.ingestFileInput) this.$refs.ingestFileInput.value = ''
    },

    runKnowledgeAction(action) {
      if (!action || !action.panel) return
      panelStore.open(action.panel, {
        source: 'knowledge_citation',
        reason: action.reason,
      })
    },

    useTool(action) {
      if (this.loading) {
        this.messages.push({
          role: 'assistant',
          content: '当前问题正在生成答复，请等回答完成后再使用资料导入、分析或报告生成工具。',
          time: this.getCurrentTime()
        })
        return
      }
      if (action === 'report') {
        this.generateReportDraft()
        return
      }
      const toolActions = {
        upload: () => {
          this.showIngestPanel = !this.showIngestPanel
        },
        location: () => {
          this.messages.push({
            role: 'assistant',
            content: '位置标注功能已启用，可在工程图上选择位置进行详细查询。',
            time: this.getCurrentTime()
          })
        },
        search: () => {
          this.messages.push({
            role: 'assistant',
            content: '已启用高级语义检索模式，支持多关键词、布尔逻辑和专业术语检索。',
            time: this.getCurrentTime()
          })
        },
        analysis: () => {
          this.messages.push({
            role: 'assistant',
            content: '数据分析功能准备就绪，可处理监测数据、计算成果、试验数据等。',
            time: this.getCurrentTime()
          })
        },
        report: () => {
          this.messages.push({
            role: 'assistant',
            content: '报告生成功能正在开发中，将支持规范符合性检查、计算书、审查意见等。',
            time: this.getCurrentTime()
          })
        }
      }

      if (toolActions[action]) {
        toolActions[action]()
      }
    },

    generateReportDraft() {
      const lastAssistant = [...this.messages].reverse().find(item => item.role === 'assistant' && item.type === 'structured')
      if (!lastAssistant) {
        this.messages.push({
          role: 'assistant',
          content: '请先完成一次设计问答，再基于答复生成报告草稿。',
          time: this.getCurrentTime()
        })
        return
      }
      const lastUserQuestion = [...this.messages].reverse().find(item => item.role === 'user')?.content || '重力坝抗滑稳定设计复核'
      const data = {
        title: '设计知识问答报告草稿',
        isReport: true,
        subtitle: '规范符合性检查 · 计算书要点 · 审查意见',
        summary: `已基于当前问答主题“${lastUserQuestion}”生成报告草稿，可继续补充计算参数、坝段位置和引用资料。`,
        voiceBrief: '已生成设计问答报告草稿，包含规范依据、符合性检查、计算书要点和审查意见四部分。',
        sections: [
          { title: '一、问题背景', items: [`问答主题：${lastUserQuestion}`, '适用场景：古贤工程设计复核、方案汇报和专家审查准备。'] },
          { title: '二、规范依据', items: ['混凝土重力坝设计规范：用于坝体及坝基抗滑稳定验算。', '工程等级与洪水标准：用于确定建筑物级别和设计/校核工况。', '工程地质勘察规范：用于确定岩体、软弱夹层和抗剪参数。'] },
          { title: '三、符合性检查', items: ['检查荷载组合是否覆盖正常、设计洪水、校核洪水及地震等工况。', '检查抗剪断与抗剪强度参数来源是否有试验或勘察依据。', '检查是否对深层滑动、双滑面或多滑面模式进行复核。'] },
          { title: '四、审查意见', items: ['建议补充坝基软弱结构面敏感性分析。', '建议将安全系数、计算方法和工程措施形成表格化汇报。', '建议把监测指标纳入后续数字孪生预警规则。'] },
        ]
      }
      this.messages.push({
        role: 'assistant',
        type: 'structured',
        content: data.summary,
        data,
        citations: [],
        actions: [],
        time: this.getCurrentTime()
      })
      this.speakBrief(data.voiceBrief)
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    close() {
      this.$emit('close')
    }
  }
}

</script>

<style scoped>
@property --angle {
  syntax: "<angle>";
  inherits: false;
  initial-value: 0deg;
}

.knowledge-qa-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at 76% 16%, rgba(255, 78, 205, 0.16), transparent 28%),
    radial-gradient(circle at 18% 78%, rgba(37, 217, 255, 0.16), transparent 30%),
    rgba(2, 4, 12, 0.76);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.knowledge-qa-modal {
  width: 1200px;
  height: 700px;
  position: relative;
  background:
    linear-gradient(135deg, rgba(6, 9, 22, 0.96), rgba(12, 24, 46, 0.94)) padding-box,
    conic-gradient(from var(--angle),
      #ff4ecd,
      #7c3cff,
      #25d9ff,
      #40ffba,
      #f8ff59,
      #ff8a3d,
      #ff4ecd) border-box;
  border-radius: 18px;
  border: 2px solid transparent;
  box-shadow:
    0 24px 90px rgba(0, 0, 0, 0.52),
    0 0 42px rgba(124, 60, 255, 0.22),
    0 0 34px rgba(37, 217, 255, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.35s cubic-bezier(.16, 1, .3, 1), rainbow-spin 14s linear infinite;
}

.knowledge-qa-modal::before {
  content: "";
  position: absolute;
  inset: 10px;
  pointer-events: none;
  border-radius: 14px;
  background:
    radial-gradient(circle at 18% 10%, rgba(255, 78, 205, 0.16), transparent 32%),
    radial-gradient(circle at 88% 22%, rgba(37, 217, 255, 0.14), transparent 30%),
    radial-gradient(circle at 50% 100%, rgba(64, 255, 186, 0.11), transparent 36%);
  mix-blend-mode: screen;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes rainbow-spin {
  to { --angle: 360deg; }
}

/* 头部样式 */
.qa-header {
  height: 60px;
  position: relative;
  background: rgba(8, 12, 28, 0.78);
  border-bottom: 1px solid rgba(255, 255, 255, 0.10);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 1;
}

.qa-header::after {
  content: "";
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: -1px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #ff4ecd, #25d9ff, #40ffba, transparent);
  opacity: 0.72;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-title::before {
  content: "";
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  border-radius: 50%;
  background:
    conic-gradient(from var(--angle),
      #ff4ecd,
      #7c3cff,
      #25d9ff,
      #40ffba,
      #f8ff59,
      #ff8a3d,
      #ff4ecd);
  box-shadow:
    0 0 14px rgba(124, 60, 255, 0.48),
    0 0 18px rgba(37, 217, 255, 0.38),
    inset rgba(7, 9, 18, 0.72) 0 0 8px 2px;
  animation: rainbow-spin 3s linear infinite;
}

.title-icon {
  font-size: 24px;
}

.title-text {
  font-size: 18px;
  font-weight: bold;
  color: #f8fbff;
  text-shadow: 0 0 16px rgba(37, 217, 255, 0.46);
}

.close-btn {
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(99, 179, 237, 0.3);
  border-radius: 50%;
  color: #76d9ff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 0 10px rgba(56, 189, 248, 0.5);
  transform: rotate(90deg);
}

/* 内容区域 */
.qa-content {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* 左侧历史记录 */
.chat-history {
  width: 250px;
  background: rgba(20, 40, 80, 0.6);
  border-right: 1px solid rgba(99, 179, 237, 0.2);
  display: flex;
  flex-direction: column;
  min-width: 250px;
}

.history-header {
  height: 50px;
  padding: 0 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  color: #76d9ff;
  font-weight: bold;
  font-size: 16px;
  flex-shrink: 0;
}

.clear-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #76d9ff;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.clear-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(99, 179, 237, 0.5);
}

.history-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(99, 179, 237, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  min-height: 50px;
}

.history-item:hover {
  background: rgba(99, 179, 237, 0.1);
  border-color: rgba(99, 179, 237, 0.3);
  transform: translateX(5px);
}

.history-item.active {
  background: rgba(99, 179, 237, 0.15);
  border-color: rgba(99, 179, 237, 0.5);
  box-shadow: 0 0 10px rgba(99, 179, 237, 0.3);
}

.history-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.history-text {
  flex: 1;
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.preset-questions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(99, 179, 237, 0.2);
  flex-shrink: 0;
}

.preset-title {
  color: #76d9ff;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
}

.preset-item {
  padding: 8px 12px;
  margin-bottom: 6px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(99, 179, 237, 0.1);
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s;
  line-height: 1.4;
}

.preset-item:hover {
  background: rgba(99, 179, 237, 0.1);
  border-color: rgba(99, 179, 237, 0.3);
  transform: translateX(5px);
}

/* 右侧对话区域 */
.chat-area {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgba(10, 25, 50, 0.3);
}

.messages-container {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  animation: messageAppear 0.3s ease;
}

@keyframes messageAppear {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.message {
  display: flex;
  gap: 12px;
  max-width: 80%;
  min-width: 0;
  align-items: flex-start;
}

.user-message {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.assistant-message {
  align-self: flex-start;
  width: 100%;
  max-width: 100%;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2a5298, #1e3c72);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}

.user-message .message-avatar {
  background: linear-gradient(135deg, #4a90e2, #357abd);
}

.message-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.user-message .message-content {
  align-items: flex-end;
}

.assistant-message .message-content {
  align-items: flex-start;
}

.user-message .message-text {
  background: linear-gradient(135deg, rgba(74, 144, 226, 0.8), rgba(53, 122, 189, 0.8));
  border: 1px solid rgba(74, 144, 226, 0.5);
  color: #fff;
  border-radius: 12px 12px 0 12px;
  padding: 12px 16px;
  line-height: 1.6;
  font-size: 14px;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: 100%;
}

.assistant-message .message-text {
  background: linear-gradient(135deg, rgba(42, 82, 152, 0.8), rgba(30, 60, 114, 0.8));
  border: 1px solid rgba(99, 179, 237, 0.3);
  color: #e0f7fa;
  border-radius: 12px 12px 12px 0;
  padding: 12px 16px;
  line-height: 1.6;
  font-size: 14px;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: 100%;
}

.rag-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.structured-answer-card {
  width: 100%;
  max-width: 820px;
  min-width: 0;
  background:
    linear-gradient(135deg, rgba(9, 22, 48, 0.96), rgba(15, 34, 58, 0.92)) padding-box,
    linear-gradient(135deg, rgba(255, 78, 205, 0.55), rgba(37, 217, 255, 0.58), rgba(64, 255, 186, 0.42)) border-box;
  border: 1px solid transparent;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28), 0 0 24px rgba(37, 217, 255, 0.10);
  color: #e8fbff;
  overflow: hidden;
}

.structured-answer-card,
.structured-answer-card * {
  box-sizing: border-box;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.structured-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 18px 12px;
  background: rgba(56, 189, 248, 0.08);
  border-bottom: 1px solid rgba(56, 189, 248, 0.18);
}

.structured-title {
  color: #7ff8ff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
}

.structured-subtitle {
  color: #9fb9c8;
  font-size: 12px;
  margin-top: 4px;
}

.structured-tools {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.voice-brief-btn {
  flex: 0 0 auto;
  min-height: 30px;
  padding: 0 12px;
  border: 1px solid rgba(56, 189, 248, 0.35);
  border-radius: 6px;
  background: rgba(0, 145, 190, 0.22);
  color: #dffbff;
  cursor: pointer;
  font-size: 12px;
}

.structured-summary {
  margin: 14px 18px;
  padding: 12px 14px;
  border-left: 3px solid #76d9ff;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  color: #f4feff;
  font-size: 14px;
  line-height: 1.7;
}

.structured-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 0 18px 16px;
}

@media (max-width: 1280px) {
  .structured-grid {
    grid-template-columns: 1fr;
  }
}

.structured-section {
  min-width: 0;
  padding: 12px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(56, 189, 248, 0.16);
  border-radius: 8px;
}

.structured-section-title {
  color: #7ff8ff;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
}

.structured-list {
  margin: 0;
  padding-left: 18px;
  color: #dceff4;
  font-size: 13px;
  line-height: 1.65;
}

.structured-list li + li {
  margin-top: 5px;
}

.structured-citations {
  padding: 10px 18px 16px;
  color: #9fb9c8;
  font-size: 12px;
  line-height: 1.6;
  border-top: 1px solid rgba(56, 189, 248, 0.12);
}

.structured-citations span {
  display: block;
  color: #7ff8ff;
  margin-bottom: 4px;
}

.rag-action-btn {
  border: 1px solid rgba(99, 179, 237, 0.35);
  border-radius: 6px;
  background: rgba(0, 150, 255, 0.18);
  color: #dffbff;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  min-height: 28px;
  padding: 7px 10px;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;
}

.rag-action-btn:hover {
  background: rgba(0, 220, 255, 0.28);
  border-color: rgba(99, 179, 237, 0.7);
  transform: translateY(-1px);
}

.message-time {
  font-size: 12px;
  color: #8c9eb5;
  margin-top: 4px;
}

/* 知识卡片样式 */
.knowledge-card {
  background: rgba(20, 40, 80, 0.8);
  border: 1px solid rgba(99, 179, 237, 0.3);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  margin-top: 8px;
  width: 100%;
  animation: cardAppear 0.5s ease;
}

@keyframes cardAppear {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.card-header {
  padding: 15px 20px;
  background: rgba(0, 100, 200, 0.4);
  border-bottom: 1px solid rgba(99, 179, 237, 0.3);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.card-title {
  color: #76d9ff;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-subtitle {
  color: #8c9eb5;
  font-size: 12px;
  font-style: italic;
}

/* 综合设计建议样式 */
.card-summary {
  padding: 15px 20px;
  background: rgba(0, 100, 200, 0.3);
  border-bottom: 1px solid rgba(99, 179, 237, 0.3);
  margin: 0;
  border-radius: 0;
}

.summary-title {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #76d9ff;
  font-size: 14px;
  font-weight: bold;
}

.summary-icon {
  font-size: 16px;
  margin-right: 6px;
}

.summary-text {
  margin-bottom: 8px;
  line-height: 1.5;
  color: #e0f7fa;
  font-size: 13px;
}

.summary-actions {
  padding: 8px;
  background: rgba(0, 150, 255, 0.1);
  border-radius: 6px;
  margin-top: 8px;
}

.action-title {
  color: #4fc3f7;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 6px;
}

.action-list {
  margin: 0 0 0 16px;
  color: #e0f7fa;
  font-size: 12px;
  line-height: 1.5;
}

.action-list li {
  margin-bottom: 4px;
}

/* 卡片部分 */
.card-section {
  padding: 15px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;
}

.card-section:hover {
  background: rgba(255, 255, 255, 0.05);
}

.card-section:last-child {
  border-bottom: none;
}

.section-title {
  color: #76d9ff;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-icon {
  font-size: 16px;
  margin-right: 6px;
}

.section-content {
  color: #e0e0e0;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 规范条文样式 */
.regulation-item,
.case-item,
.advice-item {
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border-left: 3px solid #76d9ff;
  transition: all 0.3s;
  animation: itemAppear 0.3s ease;
}

.regulation-item:hover,
.case-item:hover,
.advice-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(5px);
}

.regulation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.regulation-title {
  color: #4fc3f7;
  font-weight: bold;
  font-size: 13px;
  flex: 1;
}

.regulation-code {
  background: rgba(0, 150, 255, 0.2);
  color: #4fc3f7;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-family: 'Consolas', monospace;
}

.regulation-clause {
  margin: 6px 0;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.05);
  border-left: 2px solid #4fc3f7;
  font-size: 12px;
}

.clause-label {
  color: #8c9eb5;
  margin-right: 4px;
  font-weight: 500;
}

.clause-content {
  color: #e0e0e0;
  font-weight: 500;
}

.regulation-content {
  margin: 8px 0;
  line-height: 1.5;
  font-size: 12px;
  color: #e0e0e0;
}

.regulation-requirements {
  margin-top: 8px;
  padding: 8px;
  background: rgba(0, 100, 200, 0.1);
  border-radius: 4px;
}

.req-title {
  color: #4fc3f7;
  font-size: 12px;
  margin-bottom: 4px;
  font-weight: 500;
}

.req-list {
  margin: 4px 0 4px 16px;
  color: #e0e0e0;
  font-size: 12px;
  line-height: 1.5;
}

.req-list li {
  margin-bottom: 2px;
}

.regulation-source {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 11px;
  color: #8c9eb5;
  font-style: italic;
}

/* 工程案例样式 */
.case-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.case-name-section {
  display: flex;
  align-items: center;
  gap: 4px;
}

.case-name {
  color: #4fc3f7;
  font-weight: bold;
  font-size: 13px;
}

.case-location {
  color: #8c9eb5;
  font-size: 12px;
}

.case-tags {
  display: flex;
  gap: 8px;
  align-items: center;
}

.case-type {
  background: rgba(99, 179, 237, 0.2);
  color: #76d9ff;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  border: 1px solid rgba(99, 179, 237, 0.3);
}

.case-scale {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.case-desc {
  margin: 8px 0;
  line-height: 1.5;
  font-size: 12px;
  color: #e0e0e0;
}

.case-params {
  margin: 8px 0;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.param-title {
  color: #4fc3f7;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 6px;
}

.param-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 6px;
}

.param-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 6px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
  font-size: 12px;
}

.param-name {
  color: #8c9eb5;
}

.param-value {
  color: #e0f7fa;
  font-weight: 500;
}

.case-features {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.feature-tag {
  background: rgba(99, 179, 237, 0.1);
  color: #76d9ff;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  border: 1px solid rgba(99, 179, 237, 0.3);
  transition: all 0.3s;
}

.feature-tag:hover {
  background: rgba(99, 179, 237, 0.2);
  transform: translateY(-2px);
}

.case-lessons {
  margin-top: 8px;
  padding: 8px;
  background: rgba(255, 193, 7, 0.1);
  border-left: 3px solid #ffc107;
  border-radius: 4px;
}

.lessons-title {
  color: #ffc107;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
}

.lessons-content {
  color: #ffeaa7;
  font-size: 12px;
  line-height: 1.4;
}

/* 专家建议样式 */
.advice-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.advice-expert {
  color: #4fc3f7;
  font-weight: bold;
  font-size: 13px;
}

.advice-title {
  color: #8c9eb5;
  font-size: 12px;
  font-style: italic;
}

.advice-unit {
  color: #8c9eb5;
  font-size: 11px;
  font-style: italic;
}

.advice-content {
  margin: 6px 0;
  line-height: 1.5;
  font-size: 12px;
  color: #e0e0e0;
}

.advice-basis {
  margin: 6px 0;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  font-size: 12px;
}

.basis-title {
  color: #8c9eb5;
  font-size: 11px;
  font-weight: 500;
  margin-bottom: 2px;
}

.basis-content {
  color: #e0e0e0;
  font-style: italic;
}

.advice-recommendations {
  margin-top: 8px;
}

.rec-title {
  color: #4fc3f7;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
}

.rec-list {
  margin: 4px 0 4px 16px;
  color: #e0e0e0;
  font-size: 12px;
  line-height: 1.5;
}

.rec-list li {
  margin-bottom: 2px;
}

/* 加载中 */
.loading-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  justify-content: center;
  color: #76d9ff;
  font-size: 14px;
  align-self: center;
  width: 100%;
  text-align: center;
}

.loading-dots {
  display: flex;
  gap: 4px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #76d9ff;
  animation: dotFlashing 1s infinite alternate;
  box-shadow: 0 0 8px rgba(56, 189, 248, 0.5);
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotFlashing {
  0% { opacity: 0.3; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1.2); }
}

.loading-text {
  color: #76d9ff;
  font-size: 14px;
  text-shadow: 0 0 8px rgba(56, 189, 248, 0.5);
}

/* 输入区域 */
.input-area {
  padding: 15px 20px;
  background: rgba(20, 40, 80, 0.7);
  border-top: 1px solid rgba(99, 179, 237, 0.3);
  backdrop-filter: blur(10px);
}

.input-tools {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.tool-btn {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(99, 179, 237, 0.3);
  border-radius: 6px;
  color: #76d9ff;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.tool-btn:hover {
  background: rgba(99, 179, 237, 0.2);
  border-color: rgba(99, 179, 237, 0.5);
  box-shadow: 0 0 8px rgba(99, 179, 237, 0.4);
  transform: translateY(-2px);
}

.ingest-panel {
  margin-bottom: 10px;
  padding: 12px;
  background: rgba(6, 20, 42, 0.88);
  border: 1px solid rgba(99, 179, 237, 0.28);
  border-radius: 8px;
}

.ingest-header,
.ingest-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.ingest-header {
  margin-bottom: 10px;
}

.ingest-title {
  color: #76d9ff;
  font-size: 14px;
  font-weight: 600;
}

.ingest-grid {
  display: grid;
  grid-template-columns: minmax(140px, 220px) 1fr;
  gap: 8px;
  margin-bottom: 8px;
}

.ingest-input,
.ingest-textarea {
  width: 100%;
  background: rgba(255, 255, 255, 0.09);
  border: 1px solid rgba(99, 179, 237, 0.22);
  border-radius: 6px;
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  outline: none;
}

.ingest-input {
  height: 34px;
  padding: 0 10px;
}

.ingest-textarea {
  min-height: 64px;
  padding: 9px 10px;
  resize: vertical;
  line-height: 1.5;
}

.ingest-file-input {
  display: none;
}

.ingest-file-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  color: #9bdce8;
  font-size: 12px;
}

.ingest-file-row span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ingest-file-btn {
  border: 1px solid rgba(99, 179, 237, 0.28);
  border-radius: 6px;
  background: rgba(99, 179, 237, 0.10);
  color: #e0f7fa;
  cursor: pointer;
  font-size: 12px;
  padding: 5px 10px;
}

.ingest-file-btn.ghost {
  background: rgba(255, 255, 255, 0.06);
}

.ingest-input::placeholder,
.ingest-textarea::placeholder {
  color: #8c9eb5;
}

.ingest-status {
  min-height: 18px;
  color: #9bdce8;
  font-size: 12px;
  line-height: 1.4;
}

.ingest-close-btn,
.ingest-submit-btn {
  border: 1px solid rgba(99, 179, 237, 0.32);
  border-radius: 6px;
  color: #e0f7fa;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.ingest-close-btn {
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.08);
}

.ingest-submit-btn {
  min-width: 76px;
  height: 32px;
  background: linear-gradient(135deg, #00a9c8, #006fb0);
}

.ingest-submit-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.input-wrapper {
  position: relative;
  margin-bottom: 10px;
}

.message-input {
  width: 100%;
  min-height: 60px;
  max-height: 120px;
  padding: 12px 50px 12px 15px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(99, 179, 237, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  resize: none;
  outline: none;
  transition: all 0.3s;
  line-height: 1.5;
  font-family: inherit;
}

.message-input:focus {
  border-color: #76d9ff;
  box-shadow: 0 0 10px rgba(56, 189, 248, 0.3);
  background: rgba(255, 255, 255, 0.15);
}

.message-input::placeholder {
  color: #8c9eb5;
}

.send-btn {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #76d9ff, #0088cc);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.send-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #00e6f2, #0077bb);
  box-shadow: 0 0 15px rgba(56, 189, 248, 0.5);
  transform: translateY(-2px) scale(1.1);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.send-icon {
  font-size: 20px;
  font-weight: bold;
  transition: all 0.3s;
}

.quick-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-label {
  color: #76d9ff;
  font-size: 12px;
  white-space: nowrap;
}

.quick-btn {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(99, 179, 237, 0.3);
  border-radius: 16px;
  color: #e0f7fa;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.quick-btn:hover {
  background: rgba(99, 179, 237, 0.2);
  border-color: rgba(99, 179, 237, 0.5);
  box-shadow: 0 0 8px rgba(99, 179, 237, 0.4);
  transform: translateY(-1px);
}
</style>
